import { useMemo } from 'react';

const INTERVAL = 5.5;

interface CyclingPlaceholderProps {
  items: string[];
  fontSize?: number;
}

/**
 * Pure-CSS cycling placeholder overlay. Each item fades in, holds, then fades
 * out using staggered CSS animation-delay. Runs entirely on the compositor
 * thread — immune to React re-renders.
 */
export function CyclingPlaceholder({ items, fontSize = 15 }: CyclingPlaceholderProps) {
  const count = items.length;
  const totalDuration = count * INTERVAL;

  // Build a single @keyframes rule. Each item is visible for (100/count)% of
  // the total cycle. Within that window the item fades in, holds, then fades out.
  const keyframes = useMemo(() => {
    const seg = 100 / count;
    // Percentages within the segment — tight crossfade with minimal gap
    const fadeIn = seg * 0.04;
    const holdEnd = seg * 0.94;
    const fadeOut = seg * 0.98;

    return `@keyframes f-cycle-ph{0%{opacity:0;transform:translateY(5px)}${fadeIn}%{opacity:1;transform:translateY(0)}${holdEnd}%{opacity:1;transform:translateY(0)}${fadeOut}%{opacity:0;transform:translateY(-5px)}${seg}%,100%{opacity:0;transform:translateY(5px)}}`;
  }, [count]);

  return (
    <div
      className="pointer-events-none absolute inset-0 flex items-center overflow-hidden"
      aria-hidden
    >
      <style>{keyframes}</style>
      {items.map((text, i) => (
        <span
          key={i}
          className="absolute inset-0 flex items-center"
          style={{
            opacity: 0,
            fontSize,
            fontWeight: 450,
            color: 'var(--shell-placeholder)',
            fontFamily: 'var(--font-sans)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            animation: `f-cycle-ph ${totalDuration}s ease-in-out ${i * INTERVAL}s infinite`,
          }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}
